import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import ReactDOM from 'react-dom';
import { Focusable } from '../Focusable';
import { notifyError } from '../Notification';
import { useThemeColor } from '../Theme';
import { useTranslate } from '../util';
import { ErrorBoundary } from '../util/ErrorBoundary';
import { setDataAttributes } from '../util/getDataAttributes';
import { StyledTab, StyledTabContent, StyledTabIcon, StyledTabIndicator, StyledTabText } from './Tab.styled';
import { useTabContext } from './TabContext';
export function Tab(props) {
  var Icon = props.icon;
  var tabId = React.useRef("tabid".concat(Math.random()));
  var tabContext = useTabContextOrDefault(props);
  var registered = React.useRef(false);
  React.useEffect(function () {
    registered.current = tabContext.registerTab(props.value, {
      tabId: tabId.current,
      className: props.className
    });
    if (!registered.current) {
      console.error("[flow-web] A tab with value \"".concat(props.value, "\" has already been registered."));
    }
    return function () {
      if (!registered.current) {
        return;
      }
      tabContext.unregisterTab(props.value);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // tab props are assumed to be static, prop updates aren't supported

  var tabElementRef = React.useRef(null);
  React.useEffect(function () {
    if (!tabElementRef.current || !tabContext.observer) {
      return;
    }
    tabContext.observer.observe(tabElementRef.current);
    return function () {
      if (tabContext.observer && tabElementRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        tabContext.observer.unobserve(tabElementRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabElementRef.current, tabContext.observer]);
  var textIconColor = useThemeColor('primaryText');
  var onKeyUp = function onKeyUp(e) {
    if (e.key === 'Enter') {
      tabContext.changeTab(props.value);
    }
  };
  if (!registered.current) {
    return null;
  }
  var isCurrentTab = props.value === tabContext.activeTab;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledTab, _extends({
    fullWidth: tabContext.fullWidth,
    isLastTab: tabContext.isLastTab(props.value),
    isCurrentTab: isCurrentTab,
    id: tabId.current,
    role: "tab",
    "aria-controls": tabContext.contentId,
    onClick: function onClick() {
      return tabContext.changeTab(props.value);
    },
    "aria-selected": isCurrentTab,
    ref: tabElementRef
  }, setDataAttributes(props, 'tab')), /*#__PURE__*/React.createElement(Focusable, {
    onKeyUp: onKeyUp
  }, /*#__PURE__*/React.createElement(StyledTabContent, null, !!Icon && /*#__PURE__*/React.createElement(StyledTabIcon, null, /*#__PURE__*/React.createElement(Icon, {
    size: 14,
    color: textIconColor
  })), /*#__PURE__*/React.createElement(StyledTabText, null, props.label))), /*#__PURE__*/React.createElement(StyledTabIndicator, {
    currentTab: isCurrentTab
  })), tabContext.contentId && isCurrentTab && /*#__PURE__*/React.createElement(TabContent, {
    contentId: tabContext.contentId,
    tabId: tabId.current,
    className: props.className
  }, props.children));
}
function TabContent(props) {
  var t = useTranslate();
  var portal = document.querySelector("#".concat(props.contentId));
  if (!portal) {
    return null;
  }
  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    onError: function onError() {
      notifyError(t('citrix-ui:generic_error_message'));
    }
  }, /*#__PURE__*/ReactDOM.createPortal(props.children, portal));
}
function useTabContextOrDefault(props) {
  var _props$tabContext;
  var defaultContext = useTabContext();
  return (_props$tabContext = props.tabContext) !== null && _props$tabContext !== void 0 ? _props$tabContext : defaultContext;
}